import {
  forwardRef,
  ReactElement,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { RadioButtonChecked, ExpandMore, ExpandLess } from '@mui/icons-material'
import { Box, Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import {
  CoordinateEvent,
  coordinateEventName,
  subscribeCoordinateEvent,
  unsubscribeCoordinateEvent,
} from '@ui/events'
import { TooltipIconButton } from '@ui/feedback'
import { CoordinateNumberInput } from '@ui/forms'
import { useControlStore } from '@editorStores'
import { useSceneMarker, useSelectionMode } from '@editorHooks'
import CoordinatePointInputActionBar from './components/CoordinatePointInputActionBar'
import {
  ReplaceDecimalRoundingProvider,
  useReplaceDecimalRounding,
} from './context/ReplaceDecimalRoundingContext'
import { createDefaultPointManipulationConfig } from './pointManipulationConfig'
import { PartialPointManipulationConfig, PointAxisKey, PointManipulationConfig } from './types'
import { roundReplaceCoordinates } from './utils'

export interface CoordinatePointInputRef {
  isFocused: boolean
}

interface PointInputProps {
  label: string
  name: string
  pointManipulation?: PartialPointManipulationConfig
  disableReplace?: boolean
  onChangeX?: (value: number | null) => void
  onChangeY?: (value: number | null) => void
  onChangeZ?: (value: number | null) => void
  onReplace?: (x: number, y: number, z: number) => void
  transformSceneMarkerPoint?: (markerPoint: ImmutableVector3) => ImmutableVector3
  actions?: ReactNode
  actionsDefaultExpanded?: boolean
  coordinatePickerPosition?: 'front' | 'back'
}

const axisLabels: Record<PointAxisKey, string> = {
  x: 'X',
  y: 'Y',
  z: 'Z',
}

const CoordinatePointInputInner = forwardRef(
  (
    {
      label,
      name,
      pointManipulation,
      disableReplace = false,
      onChangeX,
      onChangeY,
      onChangeZ,
      onReplace,
      transformSceneMarkerPoint,
      actions,
      actionsDefaultExpanded = false,
      coordinatePickerPosition = 'back',
    }: PointInputProps & { pointManipulation: PointManipulationConfig },
    ref,
  ): ReactElement => {
    const { axisState } = useReplaceDecimalRounding()
    const setCoordinateExplorerEnabled = useControlStore(
      state => state.setCoordinateExplorerEnabled,
    )

    const position = useWatch({ name })

    const [xFocused, setXFocused] = useState(false)
    const [yFocused, setYFocused] = useState(false)
    const [zFocused, setZFocused] = useState(false)
    const [actionBarExpanded, setActionBarExpanded] = useState(actionsDefaultExpanded)

    const { setSelectionMode, unsetSelectionMode, selectionModeKey } = useSelectionMode()

    const isEditable = useMemo(
      () =>
        !pointManipulation.x.disabled ||
        !pointManipulation.y.disabled ||
        !pointManipulation.z.disabled,
      [pointManipulation],
    )

    const sceneMarkerPoint = useMemo(() => {
      const pVec = new ImmutableVector3(position.x, position.y, position.z)
      if (transformSceneMarkerPoint) {
        return transformSceneMarkerPoint(pVec)
      }
      return pVec
    }, [position, transformSceneMarkerPoint])

    useSceneMarker({
      marker: sceneMarkerPoint,
      enabled: isEditable && (xFocused || yFocused || zFocused || selectionModeKey === name),
      disableDepthTest: true,
    })

    useImperativeHandle(ref, () => ({ isFocused: xFocused || yFocused || zFocused }))

    const { setValue } = useFormContext()

    const selectionKey = `${coordinateEventName}-${name}`

    const getAxisDecimalScale = (axis: PointAxisKey): number | undefined =>
      axisState[axis].roundOnReplace ? axisState[axis].numDecimalsRoundingOnReplace : undefined

    useEffect(() => {
      if (selectionModeKey === selectionKey) {
        const focusEvent = (event: unknown) => {
          const { x, y, z } = (event as CoordinateEvent).detail
          // eslint-disable-next-line no-console
          console.log('x', x, 'y', y, 'z', z)
          const replaceRounded = roundReplaceCoordinates(x, y, z, {
            x: axisState.x.roundOnReplace ? axisState.x.numDecimalsRoundingOnReplace : null,
            y: axisState.y.roundOnReplace ? axisState.y.numDecimalsRoundingOnReplace : null,
            z: axisState.z.roundOnReplace ? axisState.z.numDecimalsRoundingOnReplace : null,
          })
          // eslint-disable-next-line no-console
          console.log('replaceRounded', replaceRounded)

          if (onReplace) {
            onReplace(replaceRounded.x, replaceRounded.y, replaceRounded.z)
          } else {
            setValue(name, replaceRounded)
          }
          setTimeout(() => {
            unsetSelectionMode()
          }, 100)
        }

        subscribeCoordinateEvent(focusEvent as unknown as CoordinateEvent)

        return () => {
          unsubscribeCoordinateEvent(focusEvent as unknown as CoordinateEvent)
        }
      }
    }, [
      axisState,
      name,
      onReplace,
      selectionKey,
      selectionModeKey,
      setCoordinateExplorerEnabled,
      setValue,
      unsetSelectionMode,
    ])

    const handleCopy = async () => {
      try {
        const coordinates = {
          x: position.x,
          y: position.y,
          z: position.z,
        }
        await navigator.clipboard.writeText(JSON.stringify(coordinates))
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to copy coordinates:', error)
      }
    }

    const handlePaste = async () => {
      try {
        const text = await navigator.clipboard.readText()
        const coordinates = JSON.parse(text)

        if (coordinates && typeof coordinates.x === 'number' && typeof coordinates.y === 'number') {
          const replaceRounded = roundReplaceCoordinates(
            coordinates.x,
            coordinates.y,
            typeof coordinates.z === 'number' ? coordinates.z : position.z,
            {
              x: axisState.x.roundOnReplace ? axisState.x.numDecimalsRoundingOnReplace : null,
              y: axisState.y.roundOnReplace ? axisState.y.numDecimalsRoundingOnReplace : null,
              z: axisState.z.roundOnReplace ? axisState.z.numDecimalsRoundingOnReplace : null,
            },
          )

          if (onReplace) {
            onReplace(replaceRounded.x, replaceRounded.y, replaceRounded.z)
          } else {
            setValue(name, replaceRounded)
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to paste coordinates:', error)
      }
    }

    const coordinatePicker = (
      <>
        {isEditable && (
          <TooltipIconButton
            tooltip="Wähle einen Punkt in der Scene"
            tooltipProps={{ placement: 'bottom' }}
            onClick={() => {
              if (selectionModeKey === selectionKey) {
                unsetSelectionMode()
                setCoordinateExplorerEnabled(false)
              } else {
                setSelectionMode({
                  key: selectionKey,
                  message: 'Wähle einen Punkt in der Scene',
                  onAbort: () => {
                    setCoordinateExplorerEnabled(false)
                  },
                })
                setCoordinateExplorerEnabled(true)
              }
            }}
            disabled={disableReplace}
            sx={{ padding: 0 }}
            data-cy={`coordinate-select-${name}`}
          >
            <RadioButtonChecked color={selectionModeKey === selectionKey ? 'success' : 'inherit'} />
          </TooltipIconButton>
        )}
      </>
    )

    const axisInputs: Array<{
      axis: PointAxisKey
      onChange?: (value: number | null) => void
      onFocus: () => void
      onBlur: () => void
    }> = [
      {
        axis: 'x',
        onChange: onChangeX,
        onFocus: () => setXFocused(true),
        onBlur: () => setXFocused(false),
      },
      {
        axis: 'y',
        onChange: onChangeY,
        onFocus: () => setYFocused(true),
        onBlur: () => setYFocused(false),
      },
      {
        axis: 'z',
        onChange: onChangeZ,
        onFocus: () => setZFocused(true),
        onBlur: () => setZFocused(false),
      },
    ]

    return (
      <Box pt={1} pb={0.5} px={0.5} borderRadius={1} bgcolor="grey.200">
        <Stack direction="column" spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {
              <TooltipIconButton
                tooltip={
                  actionBarExpanded ? 'Aktionsleiste ausblenden' : 'Aktionsleiste einblenden'
                }
                tooltipProps={{ placement: 'left' }}
                onClick={() => setActionBarExpanded(!actionBarExpanded)}
                sx={{ padding: 0 }}
              >
                {actionBarExpanded ? <ExpandLess /> : <ExpandMore />}
              </TooltipIconButton>
            }
            {coordinatePickerPosition === 'front' && coordinatePicker}
            {axisInputs.map(({ axis, onChange, onFocus, onBlur }) => {
              if (pointManipulation[axis].hidden) {
                return null
              }

              return (
                <CoordinateNumberInput
                  key={axis}
                  label={`${axisLabels[axis]} ${label}`}
                  size="small"
                  sx={{ width: '100%', bgcolor: 'grey.50', flexGrow: 1 }}
                  name={`${name}.${axis}`}
                  unit="m"
                  data-cy={`input-${label}-${axis}`}
                  decimalScale={getAxisDecimalScale(axis)}
                  disabled={pointManipulation[axis].disabled}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  onValueChange={onChange}
                />
              )
            })}
            {coordinatePickerPosition === 'back' && coordinatePicker}
          </Stack>

          {actionBarExpanded && (
            <CoordinatePointInputActionBar
              pointManipulation={pointManipulation}
              editable={isEditable}
              onCopy={handleCopy}
              onPaste={handlePaste}
              actions={actions}
            />
          )}
        </Stack>
      </Box>
    )
  },
)

CoordinatePointInputInner.displayName = 'CoordinatePointInputInner'

const CoordinatePointInput = forwardRef(
  ({ pointManipulation, ...props }: PointInputProps, ref): ReactElement => {
    const resolvedPointManipulation = useMemo(
      () => createDefaultPointManipulationConfig(pointManipulation),
      [pointManipulation],
    )

    return (
      <ReplaceDecimalRoundingProvider pointManipulation={resolvedPointManipulation}>
        <CoordinatePointInputInner
          ref={ref}
          {...props}
          pointManipulation={resolvedPointManipulation}
        />
      </ReplaceDecimalRoundingProvider>
    )
  },
)

CoordinatePointInput.displayName = 'CoordinatePointInput'

export { default as DefaultReplaceNumDecimalRoundingAction } from './components/DefaultReplaceNumDecimalRoundingAction'
export {
  createDefaultPointManipulationConfig,
  createHighPrecisionPointManipulationConfig,
  createUnlimitedPrecisionPointManipulationConfig,
  createReadOnlyPointManipulationConfig,
  DEFAULT_INPUT_DECIMAL_SCALE,
} from './pointManipulationConfig'
export { calculateSceneCommentCenterFromCalloutPoint, roundReplaceCoordinates } from './utils'
export {
  DEFAULT_SCENE_COMMENT_OFFSET_DIRECTION,
  DEFAULT_SCENE_COMMENT_OFFSET_DISTANCE,
  DEFAULT_NUM_DECIMALS_ROUNDING_ON_REPLACE,
  MIN_REPLACE_DECIMALS_ROUNDING,
  MAX_REPLACE_DECIMALS_ROUNDING,
} from './constants'
export type {
  PointAxisManipulationConfig,
  PointManipulationConfig,
  PartialPointManipulationConfig,
  PointAxisKey,
  RoundOnReplaceScope,
} from './types'
export type { SceneCommentOffsetDirection } from './constants'

export default CoordinatePointInput
